import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

import { formatPrice } from '../../../utils/formatters';

const ModelCard = ({ 
  model, 
  viewMode = 'grid', 
  onAddToWishlist, 
  onAddToCollection,
  onRemoveFromCollection,
  isInCollection = false,
  onQuickPreview, 
  isSelected = false, 
  onSelect,
  onAddToCart,
  isAuthenticated,
  onAuthRequired,
  isGlobalCatalog = false,
  cardLayout = null,
  shopId = null
}) => {
  // console.log('ModelCard.jsx - Model prop received:', model);

  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isHeartPending, setIsHeartPending] = useState(false);
  const [addingToCart, setAddingToCart] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [isTogglingFavorite, setIsTogglingFavorite] = useState(false);

  const layoutKey = viewMode === 'list' ? 'modelRowView' : 'modelCatalog';
  const layoutConfig = cardLayout?.layouts?.[layoutKey];

  const renderElement = (id) => {
    const conf = layoutConfig?.elements?.[id] || {};
    const widthClass = conf.width === 'half' ? 'w-[48%]' : conf.width === 'auto' ? 'w-auto' : 'w-full';
    
    let marginClass = '';
    if (conf.align === 'center') marginClass = 'mx-auto';
    else if (conf.align === 'right') marginClass = 'ml-auto';
    else if (conf.align === 'left') marginClass = 'mr-auto';

    const alignClass = conf.align === 'center' ? 'text-center justify-center' : conf.align === 'right' ? 'text-right justify-end ml-auto' : 'text-left justify-start';
    const flexAlignClass = conf.align === 'center' ? 'justify-center' : conf.align === 'right' ? 'justify-end ml-auto' : 'justify-start';

    switch (id) {
      case 'title':
        return (
          <div key={id} className={`${widthClass} ${marginClass} ${alignClass} text-lg font-bold text-foreground leading-tight hover:text-primary transition-colors mb-2 break-words`}>
            {model?.name}
          </div>
        );
      case 'manufacturer':
        return (
          <div key={id} className={`${widthClass} ${marginClass} ${alignClass} text-xs text-muted-foreground font-medium mb-1`}>
            Mfg: {model?.manufacturerName || model?.manufacturer?.name || 'OpenGadget'}
          </div>
        );
      case 'parent_model':
        return null; // Product cards themselves do not have a parent product model
      case 'store_name':
        return (
          <div key={id} className={`${widthClass} ${marginClass} ${alignClass} text-xs text-muted-foreground font-medium mb-1 flex items-center gap-1`}>
            <Icon name="Store" size={12} className="text-muted-foreground/60" />
            <span>{model?.sellerBusinessName || 'OpenGadget'}</span>
          </div>
        );
      case 'description':
        return (
          <div key={id} className={`${widthClass} ${marginClass} ${alignClass} text-sm text-muted-foreground mb-3 line-clamp-2`}>
            {model?.description}
          </div>
        );
      case 'price':
        if (isGlobalCatalog && (!model?.activeSellerCount || model?.activeSellerCount === 0)) {
          return null;
        }
        return (
          <div key={id} className={`${widthClass} ${marginClass} ${alignClass} text-xl font-extrabold text-primary mb-2`}>
            {formatPrice(model?.price)}
          </div>
        );
      case 'rating':
        if (conf.ratingStyle === 'star1') {
          return (
            <div 
              key={id}
              onClick={handleViewReviewsClick}
              className={`${widthClass} ${marginClass} flex items-center ${flexAlignClass} space-x-1 my-1 cursor-pointer hover:opacity-85 transition-opacity text-xs text-foreground font-semibold`}
              title={!isAuthenticated ? 'Log in to view reviews' : `View reviews - Average rating: ${formatRating(model?.averageRating)} from ${model?.totalRatings || 0} reviews`}
            >
              <Icon name="Star" size={14} className="text-yellow-400 fill-current" />
              <span className="text-sm font-semibold text-gray-800">{formatRating(model?.averageRating)}</span>
              <span className="text-xs text-muted-foreground font-normal">({model?.totalRatings || 0})</span>
            </div>
          );
        }
        return (
          <div 
            key={id}
            onClick={handleViewReviewsClick}
            className={`${widthClass} ${marginClass} flex items-center ${flexAlignClass} space-x-1 my-1 cursor-pointer hover:opacity-85 transition-opacity`}
            title={!isAuthenticated ? 'Log in to view reviews' : `View reviews - Average rating: ${model?.averageRating} from ${model?.totalRatings || 0} reviews`}
          >
            <div className="flex">
              {[1, 2, 3, 4, 5].map((star) => (
                <Icon 
                  key={star} 
                  name="Star" 
                  size={12} 
                  className={`${star <= Math.round(model?.averageRating || 0) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} 
                />
              ))}
            </div>
            <span className="text-[10px] text-muted-foreground">({model?.totalRatings || 0})</span>
          </div>
        );
      case 'component_count':
        return (
          <div key={id} className={`${widthClass} ${marginClass} flex ${flexAlignClass} mb-2`}>
            <div 
              style={{ 
                backgroundColor: conf.bg || '#e5e7eb', 
                color: conf.bg ? '#ffffff' : '#4b5563' 
              }} 
              className={`text-[10px] font-bold px-2.5 py-1 ${conf.pillStyle === 'box' ? 'rounded' : 'rounded-full'} border border-gray-200 inline-flex items-center backdrop-blur-sm shadow-sm whitespace-nowrap`}
            >
              {conf.showIcon !== false && <Icon name="Layers" size={10} className="mr-1 shrink-0" />}
              <span>{(model?.componentCount || 0).toLocaleString()} {conf.text || 'Components'}</span>
            </div>
          </div>
        );
      case 'views_count':
        return (
          <div key={id} className={`${widthClass} ${marginClass} flex ${flexAlignClass} mb-2`}>
            <div className="text-[10px] text-muted-foreground font-bold bg-muted px-2 py-0.5 rounded border border-border inline-block shadow-sm whitespace-nowrap">
              {(model?.combinedViews || model?.viewsCount || 0).toLocaleString()} VIEWS
            </div>
          </div>
        );
      case 'action_button':
      case 'skinny_add_to_cart':
        return (
          <div key={id} className={`${widthClass} ${marginClass} flex ${flexAlignClass} mb-2`}>
            <Button
              variant="default"
              size={id === 'skinny_add_to_cart' ? "xs" : "sm"}
              onClick={handleAddToCartClick}
              disabled={addingToCart}
              iconName={isGlobalCatalog ? (model.activeSellerCount > 0 ? "Store" : "Box") : "ShoppingCart"}
              className="whitespace-nowrap"
            >
              {isGlobalCatalog ? ((model.activeSellerCount > 0 || model.hasManufacturing || model.sellProductOnly || model.sell_product_only) ? 'Add to Cart' : 'Choose Parts') : (addingToCart ? 'Adding...' : 'Add')}
            </Button>
          </div>
        );
      default:
        return null;
    }
  };

  const renderOverlayElement = (id) => {
    const conf = layoutConfig?.elements?.[id] || {};

    switch (id) {
      case 'component_count':
        return renderElement('component_count');
      case 'featured':
        return model?.isFeatured && (
          <span key={id} style={{ backgroundColor: conf.bg }} className="px-2 py-1 text-white text-[10px] font-bold rounded-full whitespace-nowrap uppercase shadow-sm">
            {conf.text || "FEATURED"}
          </span>
        );
      case 'favorite':
        return (
          <button
            key={id}
            onClick={handleHeartClick}
            disabled={isHeartPending}
            className={`
              p-2 rounded-full transition-all duration-200 hover:scale-110
              ${isInCollection ? 'bg-red-500/10' : 'bg-background/80 backdrop-blur-sm hover:bg-background'}
              ${!isAuthenticated ? 'opacity-50 cursor-pointer' : ''}
            `}
            title={!isAuthenticated ? 'Log in to add to favorites' : (isHeartPending ? 'Processing...' : (isInCollection ? 'Remove from collection' : 'Add to collection'))}
          >
            <Icon
              name="Heart"
              size={20}
              className={`${isInCollection ? 'fill-red-500 text-red-500' : 'text-muted-foreground'}`}
            />
          </button>
        );
      case 'staff_pick':
        return model?.staffpick && (
          <span key={id} style={{ backgroundColor: conf.bg }} className="px-2 py-1 text-white text-[10px] font-bold rounded-md whitespace-nowrap uppercase shadow-sm">
            {conf.text || "STAFF PICK"}
          </span>
        );
      case 'factory_certified':
        return model?.isCertified && (
          <span key={id} style={{ backgroundColor: conf.bg }} className="px-2 py-1 text-white text-[10px] font-bold rounded-md whitespace-nowrap uppercase shadow-sm">
            {conf.text || "CERTIFIED"}
          </span>
        );
      case 'mfg_badge':
        return model?.hasManufacturing && (
          <span key={id} style={{ backgroundColor: conf.bg }} className="px-2 py-1 text-white text-[10px] font-bold rounded-md whitespace-nowrap uppercase shadow-sm flex items-center gap-1">
            <Icon name="Zap" size={10} className="fill-current" />
            {conf.text || "3D/CNC"}
          </span>
        );
      case 'compare':
        return (
          <div key={id} className="flex items-center gap-1 text-[10px] text-gray-600 bg-white/95 p-1 rounded shadow-sm border border-border">
            <input
              type="checkbox"
              checked={isSelected}
              onChange={(e) => {
                e?.stopPropagation();
                onSelect(model?.id, e?.target?.checked);
              }}
              onClick={(e) => e?.stopPropagation()}
              className="w-4 h-4 text-primary bg-white border-border rounded focus:ring-primary shadow-sm"
            />
            {conf.text || ''}
          </div>
        );
      default:
        return null;
    }
  };

  const getComplexityColor = (complexity) => {
    switch (complexity) {
      case 'low': return 'text-success bg-success/10';
      case 'medium': return 'text-warning bg-warning/10';
      case 'high': return 'text-error bg-error/10';
      case 'ultra': return 'text-destructive bg-destructive/10';
      default: return 'text-muted-foreground bg-muted';
    }
  };

  const getLicenseColor = (license) => {
    switch (license) {
      case 'free': return 'text-success bg-success/10';
      case 'personal': return 'text-primary bg-primary/10';
      case 'commercial': return 'text-accent bg-accent/10';
      case 'extended': return 'text-secondary bg-secondary/10';
      default: return 'text-muted-foreground bg-muted';
    }
  };

  const formatRating = (rating) => {
    if (typeof rating !== 'number') return '0.0';
    return rating?.toFixed(1);
  };

  const handleAddToCartClick = async (e) => {
    e?.preventDefault();
    e?.stopPropagation();

    if (isGlobalCatalog) {
      onAddToCart(model);
      return;
    }

    if (!isAuthenticated) {
      onAuthRequired('Please log in to add items to cart.');
      return;
    }

    if (addingToCart) return;
    
    setAddingToCart(true);
    try {
      await onAddToCart(model?.id);
    } catch (error) {
      console.error('Error adding to cart:', error);
    } finally {
      setAddingToCart(false);
    }
  };

  const handleViewReviewsClick = (e) => {
    e?.preventDefault();
    e?.stopPropagation();

    if (!isAuthenticated) {
      onAuthRequired('Please log in to view reviews.');
      return;
    }
    navigate(`/product-reviews-ratings?productId=${model?.id}`);
  };

  const handleQuickView = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
  };

  const handleHeartClick = async (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    
    if (!isAuthenticated) {
      onAuthRequired('Please log in to add to favorites.');
      return;
    }

    if (isHeartPending) {
      return;
    }

    setIsHeartPending(true);

    try {
      if (isInCollection) {
        await onRemoveFromCollection?.(model?.id);
      } else {
        await onAddToCollection?.(model?.id);
      }
    } catch (error) {
      console.error('❌ Heart click error:', error);
    } finally {
      setIsHeartPending(false);
    }
  };

  const handleToggleFavorite = async (e) => {
    e?.preventDefault();
    e?.stopPropagation();

    if (!isAuthenticated) {
      onAuthRequired('Please log in to add to favorites.');
      return;
    }

    if (isTogglingFavorite) return;
    
    setIsTogglingFavorite(true);
    try {
      if (isInCollection) {
        await onRemoveFromCollection?.(model?.id);
      } else {
        await onAddToCollection?.(model?.id);
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
    } finally {
      setIsTogglingFavorite(false);
    }
  };

  
  
                const formatSeoPath = (name, categoryName) => {
    if (!name) return '';
    const safeCat = (categoryName || 'product').toLowerCase().replace(/[^a-z0-9]+/g, '-');
    let prefix = `/product`;
    if (['automotive', 'motorcycles'].includes(safeCat)) prefix = '/vehicle';
    
    let processedName = name.toLowerCase();
    let year = '';
    if (prefix === '/vehicle') {
      const yearMatch = processedName.match(/\b(19\d{2}|20\d{2})\b/);
      if (yearMatch) {
        year = `/${yearMatch[1]}`;
        processedName = processedName.replace(yearMatch[0], '').trim();
      }
    }
    
    const pathSegments = processedName.replace(/[^a-z0-9\s.-]/g, '').trim().split(/\s+/).filter(Boolean).join('/');
    return `${prefix}${year}/${pathSegments}`;
  };

  const getViewerUrl = (model) => {
    const isUuid = shopId && typeof shopId === 'string' && shopId.length === 36 && shopId.includes('-');
    const shopSuffix = isUuid ? `&shopId=${shopId}` : '';
    if (model?.name) {
      const seoPath = `${formatSeoPath(model.name, model.categoryName || model.category?.name)}/parts-diagram`;
      return `${seoPath}?id=${model.id}${shopSuffix}`;
    }
    return `/3d-model-viewer?id=${model?.id}&name=${encodeURIComponent(model?.name || '')}&part_count=${model?.componentCount || 0}${shopSuffix}`;
  };

  if (layoutConfig) {
    if (viewMode === 'list') {
      return (
        <Link 
          to={getViewerUrl(model)}
          className={`
            block bg-card border border-border rounded-lg p-4 hover:shadow-soft transition-all duration-300
            ${isSelected ? 'ring-2 ring-primary border-primary' : ''}
          `}
        >
          <div className="flex items-center space-x-4">
            <div className="flex-shrink-0">
              <input
                type="checkbox"
                checked={isSelected}
                onChange={(e) => {
                  e?.preventDefault();
                  e?.stopPropagation();
                  onSelect(model?.id, e?.target?.checked);
                }}
                onClick={(e) => e?.stopPropagation()}
                className="w-4 h-4 text-primary bg-background border-border rounded focus:ring-primary"
              />
            </div>
  
            <div 
              style={{ 
                height: layoutConfig.imageHeight || '80px', 
                width: layoutConfig.imageHeight || '80px' 
              }} 
              className="flex-shrink-0 bg-muted rounded-lg overflow-hidden relative"
            >
              <Image
                src={model?.sellerImageUrls?.[0] || model?.thumbnailUrl || model?.thumbnailPath || "/assets/images/no_image.png"}
                alt={model?.name || 'Product thumbnail'}
                className="w-full h-full object-cover"
              />
              <div className="absolute top-1 left-1 flex flex-col gap-1 z-10">
                {layoutConfig.imageOverlays?.topLeft?.map(id => renderOverlayElement(id))}
              </div>
              <div className="absolute top-1 right-1 flex flex-col gap-1 z-10">
                {layoutConfig.imageOverlays?.topRight?.map(id => renderOverlayElement(id))}
              </div>
              <div className="absolute bottom-1 left-1 flex flex-col gap-1 z-10">
                {layoutConfig.imageOverlays?.bottomLeft?.map(id => renderOverlayElement(id))}
              </div>
              <div className="absolute bottom-1 right-1 flex flex-col gap-1 z-10">
                {layoutConfig.imageOverlays?.bottomRight?.map(id => renderOverlayElement(id))}
              </div>
            </div>
  
            <div className="flex-1 min-w-0 flex flex-row flex-wrap items-center gap-y-2 gap-x-3">
              {layoutConfig.contentStack?.map(id => renderElement(id))}
            </div>
          </div>
        </Link>
      );
    } else {
      return (
        <div
          className={`
            group relative bg-card border border-border rounded-lg overflow-hidden transition-all duration-200 flex h-full flex-col hover:shadow-lg hover:-translate-y-1
            ${isSelected ? 'ring-2 ring-primary' : 'border border-border'}
          `}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <Link to={getViewerUrl(model)} className="block relative aspect-square bg-muted overflow-hidden">
            <Image
              src={model?.sellerImageUrls?.[0] || model?.thumbnailUrl || model?.thumbnailPath || "/assets/images/no_image.png"}
              alt={model?.name || 'Product thumbnail'}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            />
  
            <div className="absolute top-2 left-2 flex items-center gap-2 z-20">
              {layoutConfig.imageOverlays?.topLeft?.map(id => renderOverlayElement(id))}
            </div>
            <div className="absolute top-2 right-2 flex items-center gap-2 z-20">
              {layoutConfig.imageOverlays?.topRight?.map(id => renderOverlayElement(id))}
            </div>
            <div className="absolute bottom-2 left-2 flex items-center gap-2 z-20">
              {layoutConfig.imageOverlays?.bottomLeft?.map(id => renderOverlayElement(id))}
            </div>
            <div className="absolute bottom-2 right-2 flex items-center gap-2 z-20">
              {layoutConfig.imageOverlays?.bottomRight?.map(id => renderOverlayElement(id))}
            </div>
          </Link>
  
          <div className="flex-1 p-4 flex flex-row flex-wrap items-center content-start gap-y-2 gap-x-2 w-full">
            {layoutConfig.contentStack?.map(id => renderElement(id))}
          </div>
        </div>
      );
    }
  }

  if (viewMode === 'list') {
    return (
      <Link 
        to={getViewerUrl(model)}
        className={`
          block bg-card border border-border rounded-lg p-4 hover:shadow-soft transition-all duration-300
          ${isSelected ? 'ring-2 ring-primary border-primary' : ''}
        `}
      >
        <div className="flex items-center space-x-4">
          <div className="flex-shrink-0">
            <input
              type="checkbox"
              checked={isSelected}
              onChange={(e) => {
                e?.preventDefault();
                e?.stopPropagation();
                onSelect(model?.id, e?.target?.checked);
              }}
              onClick={(e) => e?.stopPropagation()}
              className="w-4 h-4 text-primary bg-background border-border rounded focus:ring-primary"
            />
          </div>

          <div className="flex-shrink-0 w-20 h-20 bg-muted rounded-lg overflow-hidden relative">
            {/* Recommended Icon for List View */}
            {model.isBoosted && (
              <div className="absolute bottom-1 right-1 z-20 bg-blue-600 text-white p-0.5 rounded-full shadow-sm">
                <Icon name="Sparkles" size={10} />
              </div>
            )}
            <Image
              src={model?.sellerImageUrls?.[0] || model?.thumbnailUrl || model?.thumbnailPath || "/assets/images/no_image.png"}
              alt={model?.name || 'Product thumbnail'}
              className="w-full h-full object-cover"
              onLoad={() => setImageLoaded(true)}
            />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-2">
                  <h3 className="text-lg font-semibold text-foreground hover:text-primary transition-colors break-words whitespace-normal line-clamp-2">
                    {model?.name}
                  </h3>
                  <div className="flex items-center space-x-1 flex-shrink-0">
                    {model?.isFeatured && (
                      <span className="px-2 py-0.5 bg-primary text-primary-foreground text-[10px] font-bold rounded-full whitespace-nowrap uppercase shadow-sm">
                        Featured
                      </span>
                    )}
                    {model?.isCertified && (
                      <span className="px-2 py-0.5 bg-green-500 text-white text-[10px] font-bold rounded-md whitespace-nowrap uppercase shadow-sm">
                        Certified
                      </span>
                    )}
                    {model?.globalStaffpick && (
                      <span className="px-2 py-0.5 bg-purple-500 text-white text-[10px] font-bold rounded-md whitespace-nowrap uppercase shadow-sm">
                        Global Staff Pick
                      </span>
                    )}
                    {model?.staffpick && (
                      <span className="px-2 py-0.5 bg-blue-500 text-white text-[10px] font-bold rounded-md whitespace-nowrap uppercase shadow-sm">
                        Store Pick
                      </span>
                    )}
                    {model?.hasManufacturing && (
                      <span className="px-2 py-0.5 bg-orange-600 text-white text-[10px] font-bold rounded-md whitespace-nowrap uppercase shadow-sm flex items-center gap-1">
                        <Icon name="Zap" size={10} className="fill-current" />
                        3D
                      </span>
                    )}
                  </div>
                </div>
                <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                  {model?.description}
                </p>
                <div className="flex items-center space-x-4 mt-2">
                  <button 
                    onClick={handleViewReviewsClick}
                    className={`flex items-center space-x-1 hover:opacity-70 transition-opacity group ${!isAuthenticated ? 'opacity-50 cursor-pointer' : 'cursor-pointer'}`}
                    title={!isAuthenticated ? 'Log in to view reviews' : `View reviews - Average rating: ${formatRating(model?.averageRating)} from ${model?.totalRatings || 0} reviews`}
                  >
                    <Icon name="Star" size={14} className="text-warning fill-current" />
                    <span className="text-sm text-muted-foreground font-medium group-hover:text-primary transition-colors">
                      {formatRating(model?.averageRating)}
                    </span>
                    <span className="text-xs text-muted-foreground group-hover:text-primary transition-colors">
                      ({model?.totalRatings || 0} {model?.totalRatings === 1 ? 'review' : 'reviews'})
                    </span>
                  </button>
                </div>
              </div>

              <div className="flex items-center space-x-3 ml-4">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleToggleFavorite}
                  disabled={isTogglingFavorite}
                  className={`
                    ${isInCollection ? 'text-error' : 'text-muted-foreground hover:text-error'}
                    ${!isAuthenticated ? 'opacity-50 cursor-pointer' : ''}
                  `}
                  title={!isAuthenticated ? 'Log in to add to favorites' : (isInCollection ? 'Remove from favorites' : 'Add to favorites')}
                >
                  {isTogglingFavorite ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-error"></div>
                  ) : (
                    <Icon name="Heart" size={18} className={isInCollection ? 'fill-current' : ''} />
                  )}
                </Button>
              </div>
            </div>

            <div className="flex items-center justify-between mt-3">
              <div className="flex items-center space-x-2">
                {model?.complexity && (
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getComplexityColor(model?.complexity)}`}>
                    {model?.complexity}
                  </span>
                )}
                {model?.license && (
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getLicenseColor(model?.license)}`}>
                    {model?.license}
                  </span>
                )}
                {model?.animated && (
                  <span className="px-2 py-1 rounded-full text-xs font-medium text-accent bg-accent/10">
                    Animated
                  </span>
                )}
                {model?.seasonalSetting && (
                  <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-500/10 text-green-700">
                    🍂 {model.seasonalSetting}
                  </span>
                )}
                {model?.holidaySetting && (
                  <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-500/10 text-red-700">
                    🎉 {model.holidaySetting}
                  </span>
                )}
              </div>
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-1 text-xs text-muted-foreground">
                  <span className="flex items-center text-muted-foreground px-2 py-1 rounded-full text-xs font-medium bg-muted/50">
                    <Icon name="Eye" size={12} className="mr-1" />
                    {(model?.combinedViews || model?.viewsCount || 0).toLocaleString()} views
                  </span>
                  {typeof model?.addedCount === 'number' && model?.addedCount > 0 && (
                    <span className="flex items-center text-muted-foreground px-2 py-1 rounded-full text-xs font-medium bg-muted/50">
                      <Icon name="ShoppingBag" size={12} className="mr-1" />
                      {model?.addedCount?.toLocaleString()} added
                    </span>
                  )}
                  {typeof model?.componentCount === 'number' && model?.componentCount > 0 && (
                    <span className="flex items-center">
                      <Icon name="Layers" size={14} className="mr-1" />
                      {model?.componentCount} Components
                    </span>
                  )}
                </div>
                {(!isGlobalCatalog && model?.price > 0) && (
                  <div className="flex items-center gap-3 ml-2 pl-4 border-l border-border">
                    <span className="font-bold text-lg text-foreground flex items-center gap-1">
                      {formatPrice(model.price)}
                    </span>
                    <Button
                      variant="default"
                      size="sm"
                      onClick={handleAddToCartClick}
                      disabled={addingToCart}
                      iconName={isGlobalCatalog ? (model.activeSellerCount > 0 ? "Store" : "Box") : "ShoppingCart"}
                      className="z-10 relative whitespace-nowrap"
                    >
                      {isGlobalCatalog ? ((model.activeSellerCount > 0 || model.hasManufacturing || model.sellProductOnly || model.sell_product_only) ? 'Add to Cart' : 'Choose Parts') : (addingToCart ? 'Adding...' : 'Add')}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <div
      className={`
        group relative bg-card border border-border rounded-lg overflow-hidden transition-all duration-200 flex h-full
        ${viewMode === 'grid' ? 'flex-col hover:shadow-lg hover:-translate-y-1' : 'flex-row hover:shadow-md gap-4'
        }
        ${isSelected ? 'ring-2 ring-primary' : 'border border-border'}
      `}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className={viewMode === 'grid' ? 'relative aspect-square overflow-hidden' : 'relative w-48 h-48 flex-shrink-0 overflow-hidden'}>
        <Link to={getViewerUrl(model)} className="block relative aspect-square bg-muted overflow-hidden">
          
          <Image
            src={model?.sellerImageUrls?.[0] || model?.thumbnailUrl || model?.thumbnailPath || "/assets/images/no_image.png"}
            alt={model?.name || 'Product thumbnail'}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            onLoad={() => setImageLoaded(true)}
          />

          <div className={`absolute top-2 right-2 flex gap-2 transition-opacity z-20 ${
            isInCollection ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
          }`}>
            <button
              onClick={handleHeartClick}
              disabled={isHeartPending}
              className={`
                p-2 rounded-full transition-all duration-200
                ${isHeartPending ? 'cursor-wait' : 'hover:scale-110'}
                ${isInCollection ? 'bg-red-500/10' : 'bg-background/80 backdrop-blur-sm hover:bg-background'}
                ${!isAuthenticated ? 'opacity-50 cursor-pointer' : ''}
              `}
              title={!isAuthenticated ? 'Log in to add to favorites' : (isHeartPending ? 'Processing...' : (isInCollection ? 'Remove from collection' : 'Add to collection'))}
            >
              <Icon
                name="Heart"
                size={20}
                className={`
                  ${isHeartPending ? 'flashing-heart' : ''} 
                  ${isInCollection ? 'fill-red-500 text-red-500' : 'text-muted-foreground'}
                `}
              />
            </button>
          </div>

          <div 
            className="absolute top-2 left-2 flex items-center gap-2"
            onClick={(e) => e?.stopPropagation()}
          >
            <input
              type="checkbox"
              checked={isSelected}
              onChange={(e) => {
                e?.stopPropagation();
                onSelect(model?.id, e?.target?.checked);
              }}
              onClick={(e) => e?.stopPropagation()}
              className="w-4 h-4 text-primary bg-white border-border rounded focus:ring-primary shadow-sm"
            />
            {model?.isFeatured && (
              <span className="px-2 py-1 bg-primary text-primary-foreground text-[10px] font-bold rounded-full whitespace-nowrap uppercase shadow-sm">
                Featured
              </span>
            )}
            {model?.isCertified && (
              <span className="px-2 py-1 bg-green-500 text-white text-[10px] font-bold rounded-md whitespace-nowrap uppercase shadow-sm">
                Certified
              </span>
            )}
            {model?.globalStaffpick && (
              <span className="px-2 py-1 bg-purple-500 text-white text-[10px] font-bold rounded-md whitespace-nowrap uppercase shadow-sm">
                Global Staff Pick
              </span>
            )}
            {model?.staffpick && (
              <span className="px-2 py-1 bg-blue-500 text-white text-[10px] font-bold rounded-md whitespace-nowrap uppercase shadow-sm">
                Store Pick
              </span>
            )}
            {model?.hasManufacturing && (
              <span className={`px-2 ${viewMode === 'list' ? 'py-0.5' : 'py-1'} bg-orange-600 text-white text-[10px] font-bold rounded-md whitespace-nowrap uppercase shadow-sm flex items-center gap-1`}>
                <Icon name="Zap" size={10} className="fill-current" />
                3D
              </span>
            )}
          </div>

          <div className="absolute bottom-2 right-2 flex flex-col items-end gap-1">
            {model.isBoosted && (
              <div className="bg-blue-600 text-white p-1 rounded-full shadow-sm" title="Recommended for you">
                <Icon name="Sparkles" size={14} />
              </div>
            )}
            {model?.isNew && (
              <span className="px-2 py-1 bg-accent text-accent-foreground text-xs font-medium rounded-full">
                New
              </span>
            )}
          </div>

          <div className="absolute bottom-2 left-2 flex items-center space-x-2 bg-black bg-opacity-70 text-white px-2 py-1 rounded-full text-xs">
            <span className="flex items-center">
              <Icon name="Eye" size={12} className="mr-1" />
              {(model?.combinedViews || model?.viewsCount || 0).toLocaleString()} views
            </span>
            {typeof model?.addedCount === 'number' && model?.addedCount > 0 && (
              <span className="flex items-center">
                <Icon name="ShoppingBag" size={12} className="mr-1" />
                {model?.addedCount?.toLocaleString()} added
              </span>
            )}
            {typeof model?.componentCount === 'number' && model?.componentCount > 0 && (
              <span className="flex items-center">
                <Icon name="Layers" size={12} className="mr-1" />
                {model?.componentCount} components
              </span>
            )}
          </div>
        </Link>
      </div>
      
      <Link to={getViewerUrl(model)} className="p-4 flex flex-col flex-1">
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-foreground hover:text-primary transition-colors break-words whitespace-normal line-clamp-2 mb-2">
            {model?.name}
          </h3>
          
          <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
            {model?.description}
          </p>

          <div className="flex items-center justify-between mb-3">
            <button 
              onClick={handleViewReviewsClick}
              className={`flex items-center space-x-1 hover:opacity-70 transition-opacity group ${!isAuthenticated ? 'opacity-50 cursor-pointer' : 'cursor-pointer'}`}
              title={!isAuthenticated ? 'Log in to view reviews' : `View reviews - Average rating: ${formatRating(model?.averageRating)} from ${model?.totalRatings || 0} reviews`}
            >
              <Icon name="Star" size={14} className="text-warning fill-current" />
              <span className="text-sm text-muted-foreground font-medium group-hover:text-primary transition-colors">
                {formatRating(model?.averageRating)}
              </span>
              <span className="text-xs text-muted-foreground group-hover:text-primary transition-colors">
                ({model?.totalRatings || 0})
              </span>
            </button>
            {(!isGlobalCatalog && model?.price > 0) && (
              <div className="font-bold text-lg text-foreground flex items-center gap-1">
                {formatPrice(model.price)}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-end justify-between mt-auto shrink-0">
          <div className="flex flex-wrap gap-1">
            {model?.complexity && (
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getComplexityColor(model?.complexity)}`}>
                {model?.complexity}
              </span>
            )}
            {model?.license && (
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getLicenseColor(model?.license)}`}>
                {model?.license}
              </span>
            )}
            {model?.animated && (
              <span className="px-2 py-1 rounded-full text-xs font-medium text-accent bg-accent/10">
                Animated
              </span>
            )}
            {model?.seasonalSetting && (
              <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-500/10 text-green-700">
                🍂 {model.seasonalSetting}
              </span>
            )}
            {model?.holidaySetting && (
              <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-500/10 text-red-700">
                🎉 {model.holidaySetting}
              </span>
            )}
          </div>
          {(isGlobalCatalog || model?.price > 0) && (
            <div className="ml-auto pl-2">
              <Button
                variant="default"
                size="sm"
                onClick={handleAddToCartClick}
                disabled={addingToCart}
                iconName={isGlobalCatalog ? (model.activeSellerCount > 0 ? "Store" : "Box") : "ShoppingCart"}
                className="flex-shrink-0 z-10 relative whitespace-nowrap"
              >
                {isGlobalCatalog ? ((model.activeSellerCount > 0 || model.hasManufacturing || model.sellProductOnly || model.sell_product_only) ? 'Add to Cart' : 'Choose Parts') : (addingToCart ? 'Adding...' : 'Add')}
              </Button>
            </div>
          )}
        </div>
      </Link>
    </div>
  );
};

export default ModelCard;